import type { FilterTree } from "./filter-tree/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartDataPoint, ChartDataResponse, CreateChartData, ListViewChart, ListViewService, UpdateChartData } from "@/services/list-view";
import { BarChart3, Edit, Maximize2, Plus, RotateCw, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, Label as RechartsLabel, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { ChartConfigModal } from "./chart-config-modal";

type FieldOption = { value: string; label: string; type?: string };

type ListViewChartsPanelProps = {
    listViewId: number | string | null | undefined;
    canEdit: boolean;
    charts: ListViewChart[];
    fieldOptions: FieldOption[];
    // Live DRQ query reflecting the table's current filters (including
    // unsaved local changes). Charts refetch on every change so they always
    // stay aligned with the rows visible in the table.
    liveQuery?: object | null;
    // Advanced filters of the table (the filter tree), sent instead of `liveQuery` when set.
    liveFilterTree?: FilterTree | null;
    // Whether the panel is currently visible. ChartCards skip fetching while
    // the panel is hidden and trigger a fresh fetch as soon as it opens.
    isOpen: boolean;
    // Bumped by the list when the rows are reloaded without any filter change
    // (Refresh button, successful action). Filters and `isOpen` are identical
    // in that case, so without this the charts would keep showing stale data.
    refreshNonce?: number;
    onChartsChanged: () => void;
    onClose: () => void;
};

function labelForField(fieldName: string | null | undefined, fieldOptions: FieldOption[]): string {
    if (!fieldName) return "";
    return fieldOptions.find(o => o.value === fieldName)?.label || fieldName;
}

/**
 * Format a chart value for display in tooltips, labels, and tables.
 * - count → integer (no decimals)
 * - sum / avg with value_format:
 *     - "number" → max 2 decimals, thousand-separated  (e.g. "54 785,60")
 *     - "currency" → EUR formatting                   (e.g. "54 785,60 €")
 *     - "percentage" → value treated as 0..1 ratio    (e.g. 0.84 → "84 %")
 */
function formatChartValue(value: number | string | null | undefined, aggregation?: string, valueFormat?: string): string {
    if (value === null || value === undefined) return "—";
    const num = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    const locale = "fr-FR";

    if (aggregation === "count") {
        return Math.round(num).toLocaleString(locale);
    }
    if (valueFormat === "currency") {
        return num.toLocaleString(locale, {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 2
        });
    }
    if (valueFormat === "percentage") {
        return num.toLocaleString(locale, {
            style: "percent",
            maximumFractionDigits: 1
        });
    }
    return num.toLocaleString(locale, { maximumFractionDigits: 2 });
}

function valueAxisTitle(chart: ListViewChart, fieldOptions: FieldOption[]): string {
    const fieldLabel = labelForField(chart.aggregate_field, fieldOptions);
    switch (chart.aggregation) {
        case "count":
            return "Count";
        case "sum":
            return fieldLabel ? `Sum of ${fieldLabel}` : "Sum";
        case "avg":
            return fieldLabel ? `Average of ${fieldLabel}` : "Average";
        default:
            return "Value";
    }
}

const PIE_COLORS = [
    "#FCBD42", // Yellow Walter (signature)
    "#4399FF", // Bleu Walter Santé
    "#FC5E3B", // Rouge Walter
    "#A1DF61", // Vert Walter
    "#000000", // Noir Walter
    "#CDC2AA", // Gold Walter
    "#8B5A3C", // Brun terreux (extension)
    "#6B4E9B", // Violet sourd (extension)
    "#2D8F8F", // Sarcelle sourde (extension)
    "#D97757" // Terracotta (extension)
];

type ChartDisplaySize = "compact" | "expanded";

const CHART_DIMENSIONS = {
    compact: {
        pieHeight: 220,
        pieOuterRadius: 80,
        cartesianHeight: 240,
        horizontalBarRowHeight: 24,
        horizontalBarMinHeight: 240
    },
    expanded: {
        pieHeight: 420,
        pieOuterRadius: 140,
        cartesianHeight: 420,
        horizontalBarRowHeight: 32,
        horizontalBarMinHeight: 320
    }
} as const;

type NormalizedChartRow = { group: string; value: number };

function ChartDataTable({
    rows,
    chart,
    grandTotal
}: {
    rows: NormalizedChartRow[];
    chart: ListViewChart;
    grandTotal: number;
}) {
    const valueAxisLabel = chart.aggregation === "count" ? "Count" : "Value";

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Group</TableHead>
                    <TableHead className="text-right">{valueAxisLabel}</TableHead>
                    <TableHead className="text-right">Share</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row, idx) => (
                    <TableRow key={`${row.group}-${idx}`}>
                        <TableCell className="font-medium">{row.group}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatChartValue(row.value, chart.aggregation, chart.value_format)}</TableCell>
                        <TableCell className="text-right tabular-nums text-gray-500">{grandTotal > 0 ? `${((row.value / grandTotal) * 100).toFixed(1)}%` : "—"}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function EmptyState() {
    return <div className="flex h-40 items-center justify-center text-sm text-gray-500">No data for the current filters.</div>;
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-gray-500">
            <span>Unable to load the chart.</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
                <RotateCw className="mr-1 h-3 w-3" /> Retry
            </Button>
        </div>
    );
}

function ChartCard({
    chart,
    listViewId,
    canEdit,
    fieldOptions,
    liveQuery,
    liveFilterTree,
    isOpen,
    refreshNonce,
    onEdit,
    onDelete
}: {
    chart: ListViewChart;
    listViewId: number | string;
    canEdit: boolean;
    fieldOptions: FieldOption[];
    liveQuery?: object | null;
    liveFilterTree?: FilterTree | null;
    isOpen: boolean;
    refreshNonce?: number;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const [data, setData] = useState<ChartDataPoint[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Stable serialization of the live query so it participates in deps
    // without retriggering on every render (object identity).
    const liveQueryKey = JSON.stringify([liveQuery ?? null, liveFilterTree ?? null]);

    // Refetch when any field that affects the chart dataset changes:
    // - chart config (group_by, aggregation, aggregate_field)
    // - the live filters from the table (so charts always match the visible rows)
    // - `refreshNonce`, for reloads where the query is unchanged but the rows
    //   behind it are not (Refresh button, successful action)
    // chart_type is purely visual so it's intentionally NOT in the deps.
    const fetchData = useCallback(() => {
        setLoading(true);
        setError(null);
        ListViewService.chartData(listViewId, chart.id, { query: liveQuery || undefined, filterTree: liveFilterTree })
            .then((res: ChartDataResponse) => {
                setData(res.data);
            })
            .catch((err: any) => {
                setError(err?.message || "Error");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listViewId, chart.id, chart.aggregation, chart.aggregate_field, chart.group_by_field, liveQueryKey, refreshNonce]);

    const groupAxisTitle = labelForField(chart.group_by_field, fieldOptions);
    const valueAxisLabel = valueAxisTitle(chart, fieldOptions);

    // Only fetch when the panel is open. If the panel is closed and the deps
    // change (e.g. filters updated while hidden), we mark data as stale but
    // don't fire a request. The fetch fires when `isOpen` flips to true so
    // the chart is always fresh when the user actually sees it.
    useEffect(() => {
        if (!isOpen) return;
        fetchData();
    }, [fetchData, isOpen]);

    const normalizedData = useMemo(
        () =>
            (data || []).map(row => ({
                group: row.group === null || row.group === undefined ? "(empty)" : String(row.group),
                value: typeof row.value === "number" ? row.value : Number(row.value) || 0
            })),
        [data]
    );

    // For Pie charts beyond PIE_MAX_SLICES categories, keep the top
    // (PIE_MAX_SLICES - 1) and aggregate the rest into an "Other" bucket so
    // the legend stays readable on high-cardinality datasets. The full list
    // of bucketed entries stays available via the drill-down modal.
    const PIE_MAX_SLICES = 7;
    const { pieData, otherEntries, otherLabel } = useMemo(() => {
        if (normalizedData.length <= PIE_MAX_SLICES) {
            return { pieData: normalizedData, otherEntries: [] as typeof normalizedData, otherLabel: "" };
        }
        const top = normalizedData.slice(0, PIE_MAX_SLICES - 1);
        const rest = normalizedData.slice(PIE_MAX_SLICES - 1);
        const otherValue = rest.reduce((sum, row) => sum + row.value, 0);
        const label = `Other (${rest.length})`;
        return { pieData: [...top, { group: label, value: otherValue }], otherEntries: rest, otherLabel: label };
    }, [normalizedData]);

    const [otherModalOpen, setOtherModalOpen] = useState(false);
    const [expandModalOpen, setExpandModalOpen] = useState(false);
    const otherTotal = useMemo(() => otherEntries.reduce((s, r) => s + r.value, 0), [otherEntries]);
    const grandTotal = useMemo(() => normalizedData.reduce((s, r) => s + r.value, 0), [normalizedData]);

    const renderChart = (size: ChartDisplaySize = "compact") => {
        if (normalizedData.length === 0) return <EmptyState />;

        const dimensions = CHART_DIMENSIONS[size];
        const showShareInPieLabels = size === "expanded";

        if (chart.chart_type === "pie") {
            const hasOther = otherEntries.length > 0;
            return (
                <>
                    <ResponsiveContainer width="100%" height={dimensions.pieHeight}>
                        <PieChart>
                            <Tooltip wrapperStyle={{ fontSize: size === "expanded" ? 13 : 11 }} formatter={(value: any) => [formatChartValue(value, chart.aggregation, chart.value_format), valueAxisLabel]} />
                            <Legend wrapperStyle={{ fontSize: size === "expanded" ? 13 : 11, color: "#6b7280" }} />
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="group"
                                outerRadius={dimensions.pieOuterRadius}
                                label={(props: any) => {
                                    const { x, y, cx, value, percent } = props;
                                    const label = showShareInPieLabels && grandTotal > 0
                                        ? `${formatChartValue(value, chart.aggregation, chart.value_format)} (${(percent * 100).toFixed(1)}%)`
                                        : formatChartValue(value, chart.aggregation, chart.value_format);
                                    return (
                                        <text x={x} y={y} fontSize={size === "expanded" ? 12 : 11} fill="#6b7280" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central">
                                            {label}
                                        </text>
                                    );
                                }}
                            >
                                {pieData.map((row, index) => {
                                    const isOther = hasOther && row.group === otherLabel;
                                    return (
                                        <Cell
                                            key={index}
                                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                                            // onClick is scoped to the "Other" Cell only -- attaching it
                                            // on the parent <Pie> would also apply cursor:pointer to the
                                            // sibling label texts (recharts-pie-label-text) that float
                                            // outside each slice, making them appear clickable while doing
                                            // nothing and breaking tooltip activation on the labels.
                                            onClick={isOther ? () => setOtherModalOpen(true) : undefined}
                                            style={{ cursor: isOther ? "pointer" : "default" }}
                                        />
                                    );
                                })}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <p className="mt-1 text-center text-xs text-gray-500">
                        {valueAxisLabel} per {groupAxisTitle}
                        {hasOther && (
                            <>
                                {" — "}click on <strong>{otherLabel}</strong> to see the full breakdown
                            </>
                        )}
                    </p>
                </>
            );
        }

        if (chart.chart_type === "line") {
            return (
                <ResponsiveContainer width="100%" height={dimensions.cartesianHeight}>
                    <LineChart data={normalizedData} margin={{ top: 8, right: 16, bottom: 24, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="group" tick={{ fontSize: size === "expanded" ? 12 : 11 }} height={40}>
                            <RechartsLabel value={groupAxisTitle} position="insideBottom" offset={-2} style={{ fontSize: size === "expanded" ? 12 : 11, fill: "#6b7280" }} />
                        </XAxis>
                        <YAxis tick={{ fontSize: size === "expanded" ? 12 : 11 }} width={size === "expanded" ? 72 : 60}>
                            <RechartsLabel value={valueAxisLabel} angle={-90} position="insideLeft" style={{ fontSize: size === "expanded" ? 12 : 11, fill: "#6b7280", textAnchor: "middle" }} />
                        </YAxis>
                        <Tooltip formatter={(value: any) => [formatChartValue(value, chart.aggregation, chart.value_format), valueAxisLabel]} />
                        <Line type="monotone" dataKey="value" name={valueAxisLabel} stroke="#4399FF" strokeWidth={2} dot />
                    </LineChart>
                </ResponsiveContainer>
            );
        }

        if (chart.chart_type === "horizontal_bar") {
            const height = Math.max(dimensions.horizontalBarMinHeight, normalizedData.length * dimensions.horizontalBarRowHeight + 40);
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <BarChart data={normalizedData} layout="vertical" margin={{ top: 8, right: 16, bottom: 28, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: size === "expanded" ? 12 : 11 }} height={40}>
                            <RechartsLabel value={valueAxisLabel} position="insideBottom" offset={-2} style={{ fontSize: size === "expanded" ? 12 : 11, fill: "#6b7280" }} />
                        </XAxis>
                        <YAxis type="category" dataKey="group" tick={{ fontSize: size === "expanded" ? 12 : 11 }} width={size === "expanded" ? 220 : 170} interval={0}>
                            <RechartsLabel value={groupAxisTitle} angle={-90} position="insideLeft" style={{ fontSize: size === "expanded" ? 12 : 11, fill: "#6b7280", textAnchor: "middle" }} />
                        </YAxis>
                        <Tooltip formatter={(value: any) => [formatChartValue(value, chart.aggregation, chart.value_format), valueAxisLabel]} />
                        <Bar dataKey="value" name={valueAxisLabel} fill="#4399FF" />
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        // bar (default — vertical)
        return (
            <ResponsiveContainer width="100%" height={dimensions.cartesianHeight}>
                <BarChart data={normalizedData} margin={{ top: 8, right: 16, bottom: 24, left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="group" tick={{ fontSize: size === "expanded" ? 12 : 11 }} height={40}>
                        <RechartsLabel value={groupAxisTitle} position="insideBottom" offset={-2} style={{ fontSize: size === "expanded" ? 12 : 11, fill: "#6b7280" }} />
                    </XAxis>
                    <YAxis tick={{ fontSize: size === "expanded" ? 12 : 11 }} width={size === "expanded" ? 72 : 60}>
                        <RechartsLabel value={valueAxisLabel} angle={-90} position="insideLeft" style={{ fontSize: size === "expanded" ? 12 : 11, fill: "#6b7280", textAnchor: "middle" }} />
                    </YAxis>
                    <Tooltip formatter={(value: any) => [formatChartValue(value, chart.aggregation, chart.value_format), valueAxisLabel]} />
                    <Bar dataKey="value" name={valueAxisLabel} fill="#4399FF" />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    return (
        <>
            <div className="flex w-full items-center justify-between">
                <span className="text-sm font-semibold text-black">{chart.name}</span>
                <div className="flex space-x-2">
                    <Maximize2
                        className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-800"
                        aria-label="Expand chart"
                        onClick={() => setExpandModalOpen(true)}
                    />
                    {canEdit && (
                        <>
                            <Trash2 className="h-4 w-4 cursor-pointer" onClick={onDelete} />
                            <Edit className="h-4 w-4 cursor-pointer" onClick={onEdit} />
                        </>
                    )}
                </div>
            </div>
            <div className="mt-2">{loading ? <Skeleton className="h-[220px] w-full" /> : error ? <ErrorState onRetry={fetchData} /> : renderChart()}</div>

            <Dialog open={expandModalOpen} onOpenChange={setExpandModalOpen}>
                <DialogContent className="max-h-[90vh] w-[95vw] max-w-5xl overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{chart.name}</DialogTitle>
                        <DialogDescription>
                            {valueAxisLabel} per {groupAxisTitle}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="min-h-[280px]">
                        {loading ? <Skeleton className="h-[420px] w-full" /> : error ? <ErrorState onRetry={fetchData} /> : renderChart("expanded")}
                    </div>
                    {!loading && !error && normalizedData.length > 0 && (
                        <div className="max-h-[320px] overflow-y-auto border-t pt-4">
                            <ChartDataTable rows={normalizedData} chart={chart} grandTotal={grandTotal} />
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={otherModalOpen} onOpenChange={setOtherModalOpen}>
                <DialogContent className="sm:max-w-[560px]">
                    <DialogHeader>
                        <DialogTitle>{otherLabel} — full breakdown</DialogTitle>
                        <DialogDescription>
                            {otherEntries.length} categories aggregated into "Other", totaling {formatChartValue(otherTotal, chart.aggregation, chart.value_format)} (
                            {grandTotal > 0 ? Math.round((otherTotal / grandTotal) * 100) : 0}% of the chart).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[420px] overflow-y-auto">
                        <ChartDataTable rows={otherEntries} chart={chart} grandTotal={grandTotal} />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export function ListViewChartsPanel({ listViewId, canEdit, charts, fieldOptions, liveQuery, liveFilterTree, isOpen, refreshNonce, onChartsChanged, onClose }: ListViewChartsPanelProps) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editingChart, setEditingChart] = useState<ListViewChart | null>(null);

    if (!listViewId) return null;

    const handleCreate = async (data: CreateChartData | UpdateChartData) => {
        try {
            await ListViewService.createChart(listViewId, data as CreateChartData);
            toast.success("Chart added");
            onChartsChanged();
        } catch (err) {
            toast.error("Failed to create the chart");
            throw err;
        }
    };

    const handleUpdate = async (data: CreateChartData | UpdateChartData) => {
        if (!editingChart) return;
        try {
            await ListViewService.updateChart(listViewId, editingChart.id, data);
            toast.success("Chart updated");
            onChartsChanged();
        } catch (err) {
            toast.error("Failed to update the chart");
            throw err;
        }
    };

    const handleDelete = async (chart: ListViewChart) => {
        if (!window.confirm(`Delete chart "${chart.name}"?`)) return;
        try {
            await ListViewService.deleteChart(listViewId, chart.id);
            toast.success("Chart deleted");
            onChartsChanged();
        } catch (err) {
            toast.error("Failed to delete the chart");
        }
    };

    return (
        <div className="flex h-full flex-col space-y-2 overflow-y-auto overflow-x-hidden p-2 pb-32" style={{ overscrollBehavior: "contain" }}>
            <div className="mb-1.5 flex !h-10 flex-row items-center justify-between">
                <h3 className="font-semibold">Quick charts ({charts.length})</h3>
                {Boolean(onClose) && <X className="cursor-pointer rounded p-0.5 hover:bg-gray-100" size={20} onClick={onClose} />}
            </div>

            <div className="space-y-2">
                {charts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center px-6 py-12 text-center text-sm text-gray-500">
                        <BarChart3 className="mb-3 h-8 w-8 text-gray-300" />
                        <p>No chart on this view yet.</p>
                        {canEdit && <p className="mt-1 text-xs">Click "Add a chart" to create one.</p>}
                    </div>
                ) : (
                    charts.map(chart => (
                        <div key={chart.id} className="flex w-full min-w-0 flex-row items-center gap-2">
                            <div className="relative w-full min-w-0 overflow-hidden rounded-sm border bg-white p-4 text-sm">
                                <ChartCard
                                    chart={chart}
                                    listViewId={listViewId}
                                    canEdit={canEdit}
                                    fieldOptions={fieldOptions}
                                    liveQuery={liveQuery}
                                    liveFilterTree={liveFilterTree}
                                    isOpen={isOpen}
                                    refreshNonce={refreshNonce}
                                    onEdit={() => {
                                        setEditingChart(chart);
                                        setModalOpen(true);
                                    }}
                                    onDelete={() => handleDelete(chart)}
                                />
                            </div>
                        </div>
                    ))
                )}
            </div>

            {canEdit && (
                <div className="flex flex-row justify-between pt-6 text-sm text-blue-500">
                    <span
                        className="cursor-pointer hover:underline hover:underline-offset-4"
                        onClick={e => {
                            e.stopPropagation();
                            setEditingChart(null);
                            setModalOpen(true);
                        }}
                    >
                        <Plus className="mr-1 inline-block" size={14} />
                        Add a chart
                    </span>
                </div>
            )}

            <ChartConfigModal open={modalOpen} onOpenChange={setModalOpen} initialChart={editingChart} fieldOptions={fieldOptions} onSubmit={editingChart ? handleUpdate : handleCreate} />
        </div>
    );
}
